/* Initializes Flickity on [data-co-flickity] carousels (the Flickity lib loads globally via
   co-embed-scripts). Reads JSON options from the attribute, wires the theme's
   .co-flickity-button .previous/.next arrows found in the carousel's parent, and flags the
   parent [data-co-flickity-static] when there's nothing to scroll.

   Also:
   - Equal title/element height: elements marked [data-co-flickity-match] inside the cells are
     set to the tallest one's height (full content, no truncation).
   - A ResizeObserver re-lays-out (flkty.resize) + re-matches when the carousel's width
     changes. This also fixes the theme editor, where Flickity initialises before the
     re-rendered section has settled its layout.
   - Positional stagger (opt in via data-co-flickity-stagger='[{"s":..,"x":..,"y":..}, ...]' +
     groupCells:false): per-slot treatments that cycle; each cell's slot index is derived from
     its measured position so resting cells land exactly on a treatment (no mid-slide dip) and
     it morphs while sliding. Transforms the inner child, never the cell (which Flickity moves).

   Editor-resilient: re-inits on shopify:section:load, destroys on :unload. */
(function () {
  if (window.__coFlickityInit) return;
  window.__coFlickityInit = true;

  var SELECTOR = '[data-co-flickity]';

  /* Per-breakpoint variants, active while data-co-flickity-mobile-mq matches (default max-width:749px):
       data-co-flickity-mobile='{...}'         — option overrides merged on top of the base (e.g. cellAlign)
       data-co-flickity-stagger-mobile='[...]' — a mobile-specific positional-stagger value set
     Flickity reads options + the stagger at construction, so the carousel is re-initialised when the
     breakpoint is crossed (see the resize handler) for the new values to take effect cleanly. */
  function mobileState(el) {
    var mq = el.getAttribute('data-co-flickity-mobile-mq') || '(max-width: 749px)';
    return window.matchMedia(mq).matches;
  }

  function getOptions(el) {
    var options = {
      cellAlign: 'left',
      contain: true,
      groupCells: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: false
    };
    var raw = (el.getAttribute('data-co-flickity') || '').trim();
    if (raw.charAt(0) === '{') {
      try { options = Object.assign(options, JSON.parse(raw)); } catch (e) { /* keep defaults */ }
    }
    if (mobileState(el)) {
      var mraw = (el.getAttribute('data-co-flickity-mobile') || '').trim();
      if (mraw.charAt(0) === '{') {
        try { options = Object.assign(options, JSON.parse(mraw)); } catch (e) { /* ignore */ }
      }
    }
    /* autoPlay guards, for ANY carousel that opts in: never auto-advance for visitors who
       asked for reduced motion, and never in the theme editor (slides moving while a merchant
       edits a block is hostile — and the editor re-inits sections often enough that timers
       would pile up mid-edit anyway). */
    if (options.autoPlay) {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        options.autoPlay = false;
      }
      if (window.Shopify && window.Shopify.designMode) {
        options.autoPlay = false;
      }
    }
    return options;
  }

  /* Set every [data-co-flickity-match] element to the tallest one's height (no truncation). */
  function equalizeHeights(el) {
    var items = el.querySelectorAll('[data-co-flickity-match]');
    if (!items.length) return;
    var i, max = 0;
    for (i = 0; i < items.length; i++) items[i].style.minHeight = '';
    for (i = 0; i < items.length; i++) {
      if (items[i].offsetHeight > max) max = items[i].offsetHeight;
    }
    for (i = 0; i < items.length; i++) items[i].style.minHeight = max + 'px';
  }

  function init(el) {
    if (!window.Flickity || el.__flkty) return;

    /* CLS guard: Flickity's init re-parents the cells, which triggers a transient reflow that's
       counted as layout shift even though the settled height is unchanged (measured ~0.47 on the
       mobile values carousel alone). Hiding the carousel across init + the settle frame keeps that
       transient off-screen, so it isn't counted — CLS 0.74 -> ~0.07 on the homepage. The hero
       (co-home-1) is deliberately NOT hidden: it's the LCP and its entrance is timing-tuned, and
       init runs late (after parse) so a mid-view hide would flash it; its own transient is tiny
       and stays in the "good" range. A 1s failsafe reveals the carousel if init ever throws. */
    var hideForInit = el.className.indexOf('co-home-1-slider') < 0;
    var prevVisibility = el.style.visibility;
    function reveal() { if (hideForInit) el.style.visibility = prevVisibility; }
    if (hideForInit) {
      el.style.visibility = 'hidden';
      setTimeout(reveal, 1000);
    }

    var flkty = new Flickity(el, getOptions(el));
    el.__flkty = flkty;
    el.__coMobileState = mobileState(el); /* remember which breakpoint we built for (see resize) */

    var scope = el.parentElement;
    var prev = scope && scope.querySelector('.co-flickity-button.previous');
    var next = scope && scope.querySelector('.co-flickity-button.next');

    if (prev) prev.addEventListener('click', function () { flkty.previous(); });
    if (next) next.addEventListener('click', function () { flkty.next(); });

    function sync() {
      if (flkty.options.wrapAround) return;
      if (prev) prev.disabled = flkty.selectedIndex <= 0;
      if (next) next.disabled = flkty.selectedIndex >= flkty.slides.length - 1;
    }

    /* Optional positional stagger: cells morph (scale + translate) as they slide through
       evenly-spaced viewport "slots". data-co-flickity-stagger lists the per-slot treatments,
       cycling: '[{"s":<scale>,"x":<translateX %>,"y":<translateY %>}, ...]' (x and y optional,
       each a % of the frame's own width/height). Each cell's slot index is derived from its
       measured position (so a resting cell sits exactly on a treatment — no mid-slide dip — and
       it self-aligns at any cell width / breakpoint). Use groupCells:false. Transform the inner
       child, NEVER the cell — Flickity positions the cell with its own transform. */
    var treatments = null;
    /* Use the mobile value set while the mobile breakpoint matches, else the base set. Re-read on
       every (re-)init, so crossing the breakpoint swaps the morph values (see the resize re-init). */
    var staggerAttr = (mobileState(el) && el.getAttribute('data-co-flickity-stagger-mobile'))
      || el.getAttribute('data-co-flickity-stagger');
    if (staggerAttr) {
      try { treatments = JSON.parse(staggerAttr); } catch (e) { treatments = null; }
    }
    var staggerSpacing = 0;
    function measureStagger() {
      var cells = flkty.cells || [];
      if (!treatments || !cells.length) return;
      var w = cells[0].element.offsetWidth || 1;
      var mr = parseFloat(getComputedStyle(cells[0].element).marginRight) || 0;
      staggerSpacing = (w + mr) || w;          // = Flickity's cell-to-cell step (px)
    }
    function lerpTreatment(idx) {
      var n = treatments.length;
      idx = ((idx % n) + n) % n;               // wrap into [0, n) for a seamless cycle
      var a = treatments[Math.floor(idx) % n];
      var b = treatments[(Math.floor(idx) + 1) % n];
      var t = idx - Math.floor(idx);
      return {
        s: a.s + (b.s - a.s) * t,
        x: (a.x || 0) + ((b.x || 0) - (a.x || 0)) * t,
        y: a.y + (b.y - a.y) * t
      };
    }
    function applyStagger() {
      if (!treatments || !treatments.length || !staggerSpacing) return;
      var cells = flkty.cells || [];
      /* Anchor the slot grid to the carousel's cellAlign point so a RESTING cell always lands on an
         integer slot — i.e. exactly on a treatment — for ANY alignment. With left-align (cellAlign 0)
         this reduces to (left - originLeft), so desktop is unchanged. With centre-align the rest
         position is fractional, so measuring from the left edge would make a sliding cell cross a
         treatment peak mid-slide — the scale would bump up-then-down instead of moving monotonically.
         Measuring each cell's own align point against the carousel's keeps the selected (centred) cell
         at slot 0 and its neighbours at ±1, ±2… */
      var rect = el.getBoundingClientRect();
      var ca = flkty.cellAlign;
      if (typeof ca !== 'number') ca = { left: 0, center: 0.5, right: 1 }[ca] || 0;
      var alignOrigin = rect.left + ca * rect.width;
      var lefts = [], widths = [];
      for (var i = 0; i < cells.length; i++) {   // read phase first (avoid layout thrash)
        var r = cells[i].element.getBoundingClientRect();
        lefts[i] = r.left;
        widths[i] = r.width;
      }
      for (var j = 0; j < cells.length; j++) {   // then write — transform the inner wrapper
        var slot = ((lefts[j] + ca * widths[j]) - alignOrigin) / staggerSpacing; // 0,±1,±2… at rest
        var kf = lerpTreatment(slot);
        var inner = cells[j].element.firstElementChild;
        if (inner) inner.style.transform = 'translate(' + kf.x + '%, ' + kf.y + '%) scale(' + kf.s + ')';
      }
    }
    if (treatments) flkty.on('scroll', applyStagger);

    /* Equal-height cells in px — NOT a circular CSS min-height:100% (which stays pinned at the
       old height when the viewport changes, so cards don't re-fit on resize without a reload).
       Reset, measure the tallest cell's natural height, then apply it to all. */
    function equalizeCells() {
      /* Opt out with data-co-flickity-no-equalize for full-bleed / hero sliders that
         size each slide themselves (e.g. per-slide height + adaptiveHeight) — forcing
         every cell to the tallest one's height would override that intentional layout. */
      if (el.hasAttribute('data-co-flickity-no-equalize')) return;
      var cells = flkty.cells || [];
      if (cells.length < 2) return;
      var i, h, max = 0;
      for (i = 0; i < cells.length; i++) cells[i].element.style.minHeight = '';
      for (i = 0; i < cells.length; i++) {
        h = cells[i].element.offsetHeight;
        if (h > max) max = h;
      }
      for (i = 0; i < cells.length; i++) cells[i].element.style.minHeight = max + 'px';
    }

    /* a11y: Flickity marks every NON-selected cell aria-hidden — but in a multi-cell-per-view
       carousel (e.g. co-home-3: 4 cells visible, groupCells:false) that includes cells which are
       fully ON-screen. Blindly mirroring aria-hidden onto `inert` (which kills pointer events +
       focus, not just the a11y tree) then makes those visible cells unclickable — only the selected
       cell stays interactive. So key off ACTUAL visibility: a cell overlapping the carousel box
       stays interactive (and we clear Flickity's bogus aria-hidden so it isn't hidden from SR /
       flagged by Lighthouse); only genuinely off-screen cells get aria-hidden + inert. Runs after
       every (re)select / settle / scroll / refresh. */
    function syncInert() {
      var cells = flkty.cells || [];
      var vp = el.getBoundingClientRect();
      for (var i = 0; i < cells.length; i++) {
        var node = cells[i].element;
        var r = node.getBoundingClientRect();
        var onscreen = r.right > vp.left + 1 && r.left < vp.right - 1;
        if (onscreen) {
          node.removeAttribute('inert');
          if (node.getAttribute('aria-hidden') === 'true') node.removeAttribute('aria-hidden');
        } else {
          node.setAttribute('aria-hidden', 'true');
          node.toggleAttribute('inert', true);
        }
      }
    }
    /* The custom scrollbar (co-home-3-controls.js) and Flickity's own momentum glide move the
       carousel via positionSlider(), which fires only 'scroll' — not 'select' / 'settle'. So also
       re-run syncInert on 'scroll', rAF-coalesced to once per frame and guarded against a stale
       instance after re-init, so cells revealed by a scrollbar drag (or mid-glide) become
       interactive without waiting for a selectedIndex change. */
    var inertRaf = 0;
    function scheduleSyncInert() {
      if (inertRaf) return;
      inertRaf = requestAnimationFrame(function () {
        inertRaf = 0;
        if (el.__flkty === flkty) syncInert();
      });
    }

    function refresh() {
      equalizeHeights(el);   // match [data-co-flickity-match] (e.g. titles) first
      equalizeCells();       // then equalize whole cells off that
      flkty.resize();
      measureStagger();
      applyStagger();
      sync();
      syncInert();
    }

    if (scope) scope.toggleAttribute('data-co-flickity-static', flkty.slides.length <= 1);
    flkty.on('select', sync);
    flkty.on('select', syncInert);
    flkty.on('settle', syncInert);
    flkty.on('scroll', scheduleSyncInert);
    refresh();
    /* Re-run once layout settles — the editor inserts re-rendered sections in a transitional
       state, which can otherwise leave equal-height cells stretched until the next resize. Reveal
       the carousel only after this settle frame, so the init reflow stays hidden (see CLS guard). */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { if (el.__flkty === flkty) { refresh(); reveal(); } });
    });

    /* Re-lay-out when the carousel's width changes — covers responsive breakpoints AND the
       theme editor, where the section re-renders before its layout has settled (only react to
       width so flkty.resize()'s height change can't feed back into a loop). */
    if (window.ResizeObserver) {
      var lastWidth = el.clientWidth;
      flkty._ro = new ResizeObserver(function (entries) {
        var width = Math.round(entries[0].contentRect.width);
        if (width === lastWidth) return;
        lastWidth = width;
        refresh();
      });
      flkty._ro.observe(el);
    }

    /* Web fonts can change cell/title heights after they load — re-run everything then. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { if (el.__flkty === flkty) refresh(); });
    }

    /* Let bespoke per-section controls (e.g. a custom scrollbar) hook the instance. Fires on
       every (re-)init, including the editor's section:load, so listeners can re-wire cleanly. */
    el.dispatchEvent(new CustomEvent('co-flickity:init', { detail: { flickity: flkty }, bubbles: true }));
  }

  function destroy(el) {
    if (el.__flkty) {
      if (el.__flkty._ro) el.__flkty._ro.disconnect();
      el.__flkty.destroy();
      el.__flkty = null;
    }
  }

  /* Mobile-only carousels opt in with data-co-flickity-mq="<media query>": init only while
     the query matches, tear down otherwise (e.g. a static multi-column grid on desktop that
     becomes a swipeable carousel on mobile). Non-gated carousels init directly, unchanged. */
  function setup(el) {
    var mq = el.getAttribute('data-co-flickity-mq');
    if (!mq) { init(el); return; }
    if (window.matchMedia(mq).matches) init(el); else destroy(el);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(setup);
  }

  /* Re-evaluate mobile-only carousels when the viewport crosses their breakpoint. Queries the
     live DOM each time, so re-rendered/removed sections never leave behind orphaned handlers. */
  var mqTimer = null;
  window.addEventListener('resize', function () {
    if (mqTimer) clearTimeout(mqTimer);
    mqTimer = setTimeout(function () {
      document.querySelectorAll(SELECTOR + '[data-co-flickity-mq]').forEach(setup);
      /* Re-init carousels with a breakpoint variant when they cross their mobile query, so the
         construction-time values (cellAlign, the stagger set) get reapplied. */
      document.querySelectorAll(SELECTOR + '[data-co-flickity-mobile],' + SELECTOR + '[data-co-flickity-stagger-mobile]').forEach(function (el) {
        if (el.__flkty && mobileState(el) !== el.__coMobileState) {
          destroy(el);
          setup(el);
        }
      });
    }, 150);
  });

  function whenReady(cb) {
    if (window.Flickity) return cb();
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Flickity) {
        clearInterval(timer);
        cb();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  }

  function start() {
    whenReady(function () { initAll(document); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (e) {
    /* Block add/remove/reorder re-renders the section — tear down any stale instance (the
       carousel element may be reused, which the init guard would otherwise skip) and rebuild
       fresh so cells re-measure cleanly; otherwise equal-height cells stay stretched until save. */
    whenReady(function () {
      e.target.querySelectorAll(SELECTOR).forEach(function (node) {
        destroy(node);
        setup(node);
      });
    });
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll(SELECTOR).forEach(destroy);
  });

  /* Theme editor: keep the carousel on the slide being edited. Selecting a block fires
     shopify:block:select on its element; if that element is a cell in one of our
     carousels, navigate to it — otherwise editing slide 2, 3… is awkward (the carousel
     sits on slide 1). The cell must carry {{ block.shopify_attributes }} for the event
     to target it. Polls briefly for the Flickity instance in case the selection races
     the section's (re-)init on load. Instant on initial load, animated on later clicks. */
  document.addEventListener('shopify:block:select', function (e) {
    var carousel = e.target.closest && e.target.closest(SELECTOR);
    if (!carousel) return;
    var tries = 0;
    (function select() {
      if (carousel.__flkty) {
        carousel.__flkty.selectCell(e.target, false, e.detail && e.detail.load);
      } else if (++tries <= 40) {
        setTimeout(select, 50);
      }
    })();
  });
})();
